import React, { useContext, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';

import {
  useCaseLock,
  useCaseLockActionGuard,
} from '../../../common/caseLocking/CaseLockContext';
import { FlexRow } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { FeatureFlags } from '../../../common/constants/featureFlags';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { placeLabPiqOrder } from '../../aps/api';
import { ITask } from '../../tasks/interfaces/ITask';
import { LabPiqture } from '../../tasks/types/labPiqture';
import styles from '../styles.module.scss';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskDetailsItem } from './components/TaskDetailsItem';

export const LabPiQDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
  applicationId,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  applicationId?: number;
}) => {
  const { flags } = useContext(FeatureFlagsContext);
  const { isLockedForActions } = useCaseLock();
  const assertCaseActionAllowed = useCaseLockActionGuard();
  const isAutoOrderingEnabled =
    flags[FeatureFlags.UW_NORA_EHR_LABPIQ_AUTO_ORDER_ENABLED] || false;
  const [ordering, setOrdering] = useState(false);
  const [orderStatus, setOrderStatus] = useState<'idle' | 'success' | 'error'>(
    'idle',
  );
  const [orderError, setOrderError] = useState('');

  const data: LabPiqture = (task.taskData ?? {}) as LabPiqture;
  const evidence = task.evidence;
  const evidenceReasons: string[] = (data?.reason || []).map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  const handlePlaceOrder = async () => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    if (!applicationId) {
      setOrderError('Application ID not available');
      return;
    }

    // Prevent multiple rapid clicks
    if (ordering) {
      return;
    }

    setOrdering(true);
    setOrderError('');

    try {
      await placeLabPiqOrder(applicationId!, task.id);
      setOrderStatus('success');
    } catch (error: any) {
      setOrderStatus('error');
      const errorMessage = error.message || 'Failed to place LabPiQ order';
      setOrderError(errorMessage);
    } finally {
      setOrdering(false);
    }
  };

  return (
    <Box data-label={ElementLocators.LOCATOR_LAB_PIQTURE_SECTION}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
        mb={0.75}
        id={task.id.toString()}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={0.5}>
            <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
          </Box>
          <Typography variant="h2">
            {Resource.TASK_TITLE_LAB_PIQTURE}
          </Typography>
        </Box>
        <Box>
          <Typography color="textSecondary" variant="subtitle2">
            {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
          </Typography>
        </Box>
      </Box>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {evidenceExists(evidence) && evidence && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}

      {/* Show upload button:
          - Show upload when task is ORDERED (or other non-REQUESTED/non-COMPLETED states) and no evidence exists
          - Do NOT show upload button when task is in REQUESTED state
      */}
      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED &&
        !evidenceExists(evidence) && (
          <FileUpload
            leftItem={Resource.FILES}
            selectedFiles={onFileSelection}
            type={DocumentType.LAB_PIQTURE}
            ongoingUpload={task.ongoingUpload?.[DocumentType.LAB_PIQTURE]}
          />
        )}

      {/* Manual ordering button - show when feature flag is ON and task is ERROR (auto-order failed) */}
      {isAutoOrderingEnabled &&
        task.state === TaskState.ERROR &&
        !isLockedForActions && (
          <Box mt={3}>
            <Alert severity="info" sx={{ mb: 2 }}>
              This LabPiQ order needs to be placed manually. Click the button
              below to order from ExamOne.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handlePlaceOrder}
              disabled={ordering || orderStatus === 'success'}
              startIcon={
                ordering && <CircularProgress size={20} color="inherit" />
              }
            >
              {orderStatus === 'success'
                ? 'Order Placed ✓'
                : 'Order LabPiQ from ExamOne'}
            </Button>

            {orderStatus === 'success' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                LabPiQ order successfully placed with ExamOne
              </Alert>
            )}

            {orderStatus === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {orderError}
              </Alert>
            )}
          </Box>
        )}

      {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {data?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={data.reasonDescription}
            />
          )}
        </>
      )}
    </Box>
  );
};
